import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { get_static_dataset_url } from '@/utils/urls.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import {
  AUTOMATION_LATEST_VERSION_KEY,
  AUTOMATION_VERSIONS_KEY,
  STATIC_DATASETS_KEY,
  STATIC_DATASET_USAGE_KEY,
} from '@/utils/queryKeys.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';

type props = {
  staticDatasetId: string;
  expectedDatasetId: string;
};

const useDeleteStaticDataset = (
  options: UseMutationOptions<void, AxiosError, props>,
): UseMutationResult<void, AxiosError, props> => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const automationId = useAutomationId();

  return useMutation<void, AxiosError, props>({
    ...options,
    onError: (error, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({ queryKey: STATIC_DATASET_USAGE_KEY(automationId) });
      void queryClient.invalidateQueries({ queryKey: STATIC_DATASETS_KEY(automationId) });
      options.onError?.(error, variables, onMutateResult, context);
    },
    mutationFn: async ({ staticDatasetId, expectedDatasetId }: props) => {
      await apiClient.delete(get_static_dataset_url(staticDatasetId), {
        params: { expected_dataset_id: expectedDatasetId },
      });
      void queryClient.invalidateQueries({ queryKey: STATIC_DATASET_USAGE_KEY(automationId) });
      await queryClient.invalidateQueries({ queryKey: STATIC_DATASETS_KEY(automationId) });
      await queryClient.invalidateQueries({
        queryKey: AUTOMATION_LATEST_VERSION_KEY(automationId),
      });
      await queryClient.invalidateQueries({ queryKey: AUTOMATION_VERSIONS_KEY(automationId) });
    },
  });
};

export default useDeleteStaticDataset;
